import { useCallback, useEffect, useState } from "react";
import { authFetchJson } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  EXTERNAL_ACTOR_NOTE,
  auditActionLabel,
  hasExternalActor,
  isExternalActor,
} from "./lexiconAuditDisplay";

type AuditEntry = {
  id: number;
  lexicon_id: string | null;
  op: string;
  actor: string | null;
  note: string;
  created_at: string;
};

export function AdminLexiconAudit() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await authFetchJson<{ entries: AuditEntry[] }>(
        "/api/admin/lexicon/audit?limit=100",
      );
      setEntries(res.entries ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>;
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-[var(--cs-ink-muted)]">
            <th className="py-2">Wanneer</th>
            <th>Wie</th>
            <th>Actie</th>
            <th>Toelichting</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className="border-t border-[var(--cs-line)]">
              <td className="py-2">{formatDateTime(entry.created_at)}</td>
              <td title={isExternalActor(entry.actor) ? EXTERNAL_ACTOR_NOTE : undefined}>
                {entry.actor ?? "—"}
              </td>
              <td>{auditActionLabel(entry.op)}</td>
              <td className="text-xs text-[var(--cs-ink-muted)]">{entry.note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasExternalActor(entries) ? (
        <p className="text-xs text-[var(--cs-ink-muted)]">{EXTERNAL_ACTOR_NOTE}</p>
      ) : null}
    </div>
  );
}
